import { Empty } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList, ListChildComponentProps, ListOnItemsRenderedProps } from 'react-window';

import { TableFactoryRowModel } from 'stores/common';
import { ITableFactoryStore } from 'stores/common/table/TableFactoryStore/interface';

import { SkeletonRow } from './SkeletonRow';
import s from './VirtualTableBody.module.scss';

const ROW_HEIGHT = 77;

type VirtualTableBodyProps = {
  columns: ColumnsType<TableFactoryRowModel>;
  scroll: { y: number };
  /** @deprecated Use rowIds + store for virtualized mode */
  dataSource?: TableFactoryRowModel[];
  /** Row IDs for virtualized mode (lazy model creation) */
  rowIds?: string[];
  /** Store for virtualized mode (lazy model creation) */
  store?: ITableFactoryStore;
  headerRef?: React.RefObject<HTMLDivElement>;
  loading?: boolean;
};

type VirtualRowData = {
  columns: ColumnsType<TableFactoryRowModel>;
  /** For legacy mode */
  dataSource?: TableFactoryRowModel[];
  /** For virtualized mode */
  rowIds?: string[];
  store?: ITableFactoryStore;
  totalWidth: number;
  firstColumnWidth: number;
};

/**
 * Get row model from data - supports both legacy and virtualized modes.
 */
const getRowModel = (
  data: VirtualRowData,
  index: number
): TableFactoryRowModel | null => {
  // Virtualized mode: lazy creation via store
  if (data.rowIds && data.store) {
    const rowId = data.rowIds[index];
    return data.store.getRowModel(rowId);
  }

  // Legacy mode: direct access from dataSource
  if (data.dataSource) {
    return data.dataSource[index] ?? null;
  }

  return null;
};

// Компонент для основных строк (без первой колонки)
const VirtualRow: React.FC<ListChildComponentProps<VirtualRowData>> = ({
  index,
  style,
  data,
}) => {
  const { columns, totalWidth, firstColumnWidth } = data;
  const record = getRowModel(data, index);

  if (!record) {
    return null;
  }

  return (
    <div className={s.virtualRowOuter} style={style}>
      <div className={s.virtualRow} style={{ width: totalWidth - firstColumnWidth }}>
        {columns.slice(1).map((column, colIndex) => {
          const dataIndex = (column as { dataIndex?: string }).dataIndex;
          const width = column.width || 200;

          const cellContent =
            typeof column.render === 'function'
              ? column.render(
                  dataIndex ? record[dataIndex as keyof TableFactoryRowModel] : undefined,
                  record,
                  index
                )
              : dataIndex
                ? record[dataIndex as keyof TableFactoryRowModel]
                : null;

          return (
            <div
              key={column.key || dataIndex || colIndex}
              className={s.virtualCell}
              style={{
                width: typeof width === 'number' ? width : 200,
                minWidth: typeof width === 'number' ? width : 200,
              }}
            >
              {cellContent}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Компонент для фиксированной первой колонки
const FixedColumnRow: React.FC<ListChildComponentProps<VirtualRowData>> = ({
  index,
  style,
  data,
}) => {
  const { columns, firstColumnWidth } = data;
  const record = getRowModel(data, index);
  const firstColumn = columns[0];

  if (!record || !firstColumn) {
    return null;
  }

  const dataIndex = (firstColumn as { dataIndex?: string }).dataIndex;

  const cellContent =
    typeof firstColumn.render === 'function'
      ? firstColumn.render(
          dataIndex ? record[dataIndex as keyof TableFactoryRowModel] : undefined,
          record,
          index
        )
      : dataIndex
        ? record[dataIndex as keyof TableFactoryRowModel]
        : null;

  return (
    <div className={s.fixedRowOuter} style={style}>
      <div
        className={s.fixedColumnCell}
        style={{
          width: firstColumnWidth,
          minWidth: firstColumnWidth,
          height: ROW_HEIGHT,
        }}
      >
        {cellContent}
      </div>
    </div>
  );
};

const VirtualTableBody: React.FC<VirtualTableBodyProps> = ({
  columns,
  scroll,
  dataSource,
  rowIds,
  store,
  headerRef,
  loading = false,
}) => {
  const { t } = useTranslation();
  const mainListRef = React.useRef<FixedSizeList>(null);
  const fixedListRef = React.useRef<FixedSizeList>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const mainListOuterRef = React.useRef<HTMLDivElement>(null);
  const fixedColumnContainerRef = React.useRef<HTMLDivElement>(null);

  // Determine if we're in virtualized mode
  const isVirtualizedMode = Boolean(rowIds && store);

  // Get item count based on mode
  const itemCount = isVirtualizedMode
    ? (rowIds?.length ?? 0)
    : (dataSource?.length ?? 0);

  // Вычисляем ширину первой колонки
  const firstColumnWidth = React.useMemo(() => {
    const width = columns[0]?.width;
    return typeof width === 'number' ? width : 200;
  }, [columns]);

  // Вычисляем общую ширину
  const totalWidth = React.useMemo(() => {
    return columns.reduce((sum, col) => {
      const width = col.width;
      return sum + (typeof width === 'number' ? width : 200);
    }, 0);
  }, [columns]);

  const itemData: VirtualRowData = React.useMemo(
    () => ({
      columns,
      dataSource,
      rowIds,
      store,
      totalWidth,
      firstColumnWidth,
    }),
    [columns, dataSource, rowIds, store, totalWidth, firstColumnWidth]
  );

  // Track visible rows for cleanup (virtualized mode only)
  const handleItemsRendered = React.useCallback(
    ({ visibleStartIndex, visibleStopIndex }: ListOnItemsRenderedProps) => {
      if (!isVirtualizedMode || !rowIds || !store) {
        return;
      }

      // Include overscan rows in visible set
      const overscan = 5;
      const startIndex = Math.max(0, visibleStartIndex - overscan);
      const endIndex = Math.min(rowIds.length - 1, visibleStopIndex + overscan);

      const visibleIds = rowIds.slice(startIndex, endIndex + 1);
      store.setVisibleRows(visibleIds);
    },
    [isVirtualizedMode, rowIds, store]
  );

  // Показываем skeleton при загрузке и пустом dataSource
  const showSkeleton = loading && itemCount === 0;

  // Синхронизация вертикального скролла между основным списком и фиксированной колонкой
  React.useEffect(() => {
    // Пропускаем установку listener в skeleton-режиме
    if (showSkeleton) return;

    const mainListOuter = mainListOuterRef.current;
    if (!mainListOuter) return;

    const handleScroll = () => {
      const scrollTop = mainListOuter.scrollTop;
      if (fixedListRef.current) {
        fixedListRef.current.scrollTo(scrollTop);
      }
    };

    mainListOuter.addEventListener('scroll', handleScroll);
    return () => {
      mainListOuter.removeEventListener('scroll', handleScroll);
    };
  }, [showSkeleton]);

  // Синхронизация горизонтального скролла body -> header
  const handleBodyScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    if (headerRef?.current) {
      const headerScrollContainer = headerRef.current.querySelector('.ant-table-content');
      if (headerScrollContainer) {
        headerScrollContainer.scrollLeft = scrollLeft;
      }
    }
  }, [headerRef]);

  // Синхронизация скролла header -> body
  React.useEffect(() => {
    if (!headerRef?.current) return;

    const headerScrollContainer = headerRef.current.querySelector('.ant-table-content');
    if (!headerScrollContainer) return;

    const handleHeaderScroll = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = headerScrollContainer.scrollLeft;
      }
    };

    headerScrollContainer.addEventListener('scroll', handleHeaderScroll);
    return () => {
      headerScrollContainer.removeEventListener('scroll', handleHeaderScroll);
    };
  }, [headerRef]);

  // Обработка wheel на фиксированной колонке — передаём скролл в основной список
  React.useEffect(() => {
    // Пропускаем в skeleton-режиме
    if (showSkeleton) return;

    const fixedContainer = fixedColumnContainerRef.current;
    const mainListOuter = mainListOuterRef.current;
    if (!fixedContainer || !mainListOuter) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      mainListOuter.scrollTop += e.deltaY;
    };

    fixedContainer.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      fixedContainer.removeEventListener('wheel', handleWheel);
    };
  }, [showSkeleton]);

  // Количество skeleton-строк для заполнения видимой области
  const skeletonRowsCount = React.useMemo(() => {
    return Math.ceil((scroll.y || 400) / ROW_HEIGHT);
  }, [scroll.y]);

  // Рендер skeleton-строк
  if (showSkeleton) {
    return (
      <div className={s.virtualTableWrapper}>
        {/* Skeleton для фиксированной колонки */}
        <div className={s.fixedColumnContainer} style={{ width: firstColumnWidth }}>
          <div style={{ height: scroll.y || 400, overflow: 'hidden' }}>
            {Array.from({ length: skeletonRowsCount }).map((_, index) => (
              <SkeletonRow
                key={index}
                columnsCount={columns.length}
                firstColumnWidth={firstColumnWidth}
                totalWidth={totalWidth}
                rowHeight={ROW_HEIGHT}
                isFixedColumn
              />
            ))}
          </div>
        </div>

        {/* Skeleton для основной таблицы */}
        <div
          className={s.virtualTableBody}
          style={{ marginLeft: firstColumnWidth }}
        >
          <div style={{ width: totalWidth - firstColumnWidth, minWidth: '100%', height: scroll.y || 400, overflow: 'hidden' }}>
            {Array.from({ length: skeletonRowsCount }).map((_, index) => (
              <SkeletonRow
                key={index}
                columnsCount={columns.length}
                firstColumnWidth={firstColumnWidth}
                totalWidth={totalWidth}
                rowHeight={ROW_HEIGHT}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Показываем заглушку если нет данных после фильтрации
  if (itemCount === 0) {
    return (
      <div className={s.virtualTableWrapper}>
        <div className={s.emptyState} style={{ height: scroll.y || 400 }}>
          <Empty description={t('table.noData')} />
        </div>
      </div>
    );
  }

  return (
    <div className={s.virtualTableWrapper}>
      {/* Фиксированная первая колонка */}
      <div ref={fixedColumnContainerRef} className={s.fixedColumnContainer} style={{ width: firstColumnWidth }}>
        <FixedSizeList
          ref={fixedListRef}
          height={scroll.y || 400}
          itemCount={itemCount}
          itemSize={ROW_HEIGHT}
          width={firstColumnWidth}
          itemData={itemData}
          overscanCount={5}
          className={s.fixedColumnList}
          style={{ overflow: 'hidden' }}
        >
          {FixedColumnRow}
        </FixedSizeList>
      </div>

      {/* Основная таблица с горизонтальным скроллом */}
      <div 
        ref={scrollContainerRef}
        className={s.virtualTableBody}
        onScroll={handleBodyScroll}
        style={{ marginLeft: firstColumnWidth }}
      >
        <div style={{ width: totalWidth - firstColumnWidth, minWidth: '100%' }}>
          <FixedSizeList
            ref={mainListRef}
            outerRef={mainListOuterRef}
            height={scroll.y || 400}
            itemCount={itemCount}
            itemSize={ROW_HEIGHT}
            width="100%"
            itemData={itemData}
            overscanCount={5}
            className={s.virtualList}
            onItemsRendered={handleItemsRendered}
          >
            {VirtualRow}
          </FixedSizeList>
        </div>
      </div>
    </div>
  );
};

export default observer(VirtualTableBody);
